import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  ButtonGroup,
  Banner,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface DayData {
  date: string;
  deliveryCount: number;
  shipCount: number;
}

interface CalendarStats {
  ordersWithoutDates: number;
  overdueForShipment: number;
  overdueForDelivery: number;
}

export default function ShippingCalendar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi, error } = useApi();

  // Initialize month from URL date parameter if provided
  const initialDate = searchParams.get('date');
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialDate) {
      const parsed = new Date(initialDate);
      if (!isNaN(parsed.getTime())) {
        return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
      }
    }
    return new Date();
  });
  const [selectedDate] = useState<string | null>(initialDate);
  const [dateType, setDateType] = useState<'ship' | 'delivery'>('delivery');
  const [viewType, setViewType] = useState<'summary' | 'detailed'>('summary');
  const [calendarData, setCalendarData] = useState<DayData[]>([]);
  const [stats, setStats] = useState<CalendarStats>({
    ordersWithoutDates: 0,
    overdueForShipment: 0,
    overdueForDelivery: 0,
  });

  useEffect(() => {
    loadCalendarData();
  }, [currentMonth]);

  async function loadCalendarData() {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const data = await fetchApi<{ days: DayData[]; stats: CalendarStats }>(
      `/calendar?year=${year}&month=${month}`
    );

    if (data) {
      setCalendarData(data.days || []);
      setStats(data.stats || { ordersWithoutDates: 0, overdueForShipment: 0, overdueForDelivery: 0 });
    }
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday

    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);

  const monthName = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const getDataForDay = (day: number): DayData | undefined => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.find(d => d.date === dateStr);
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isSelectedDay = (day: number) => {
    if (!selectedDate) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === selectedDate;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const renderCalendarGrid = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const cells = [];

    // Header row
    cells.push(
      <div key="header" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '1px' }}>
        {days.map(day => (
          <div key={day} style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#637381' }}>
            {day}
          </div>
        ))}
      </div>
    );

    // Calendar cells
    const totalCells = Math.ceil((startingDay + daysInMonth) / 7) * 7;
    const calendarCells = [];

    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - startingDay + 1;
      const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
      const dayData = isValidDay ? getDataForDay(dayNumber) : undefined;
      const count = dateType === 'delivery' ? dayData?.deliveryCount : dayData?.shipCount;

      const isSelected = isValidDay && isSelectedDay(dayNumber);
      calendarCells.push(
        <div
          key={i}
          style={{
            padding: '8px',
            minHeight: '80px',
            border: isSelected ? '2px solid #4a9b8c' : '1px solid #e1e3e5',
            borderRadius: '8px',
            background: isSelected ? '#e8f5f2' : isToday(dayNumber) ? '#f0f5ff' : isValidDay ? 'white' : '#f6f6f7',
            cursor: isValidDay && count ? 'pointer' : 'default',
            position: 'relative',
            boxShadow: isSelected ? '0 2px 8px rgba(74, 155, 140, 0.2)' : 'none',
          }}
          onClick={() => {
            if (isValidDay && count) {
              const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`;
              navigate(`/orders?delivery_date=${dateStr}`);
            }
          }}
        >
          {isValidDay && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text as="span" variant="bodyMd" fontWeight={isToday(dayNumber) || isSelected ? 'bold' : 'regular'}>
                  {dayNumber}
                </Text>
                {isToday(dayNumber) && (
                  <Badge tone="info">Today</Badge>
                )}
                {isSelected && !isToday(dayNumber) && (
                  <Badge tone="success">Selected</Badge>
                )}
              </div>
              <div style={{ marginTop: '8px' }}>
                <Text as="span" variant="bodySm" tone="subdued">
                  {dateType === 'delivery' ? 'Deliver' : 'Ship'}{' '}
                </Text>
                <Text as="span" variant="bodyMd" fontWeight="bold">
                  {count || 0}
                </Text>
              </div>
            </>
          )}
        </div>
      );
    }

    cells.push(
      <div key="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {calendarCells}
      </div>
    );

    return cells;
  };

  // Use local date (not UTC) so "today" matches the user's wall clock
  const todayDate = new Date();
  const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;

  return (
    <Page
      title="Shipping Calendar"
      primaryAction={{
        content: "Today's Deliveries",
        onAction: () => navigate(`/orders?delivery_date=${todayStr}`),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {/* Controls */}
              <InlineStack align="space-between">
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Date type:</Text>
                  <ButtonGroup variant="segmented">
                    <Button
                      pressed={dateType === 'ship'}
                      onClick={() => setDateType('ship')}
                    >
                      Ship date
                    </Button>
                    <Button
                      pressed={dateType === 'delivery'}
                      onClick={() => setDateType('delivery')}
                    >
                      Delivery date
                    </Button>
                  </ButtonGroup>
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">View:</Text>
                  <ButtonGroup variant="segmented">
                    <Button
                      pressed={viewType === 'summary'}
                      onClick={() => setViewType('summary')}
                    >
                      Summary
                    </Button>
                    <Button
                      pressed={viewType === 'detailed'}
                      onClick={() => setViewType('detailed')}
                    >
                      Detailed
                    </Button>
                  </ButtonGroup>
                </BlockStack>
              </InlineStack>

              {/* Month Navigation */}
              <InlineStack align="space-between">
                <Button onClick={goToPreviousMonth}>Previous</Button>
                <Text as="h2" variant="headingLg">{monthName}</Text>
                <Button onClick={goToNextMonth}>Next</Button>
              </InlineStack>

              {/* Calendar Grid */}
              {renderCalendarGrid()}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Orders without Delivery dates</Text>
                <Text as="p" variant="heading2xl">{stats.ordersWithoutDates}</Text>
                <Button
                  variant="plain"
                  onClick={() => navigate('/orders?no_date=true')}
                >
                  View Orders
                </Button>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Orders overdue for shipment</Text>
                <Text as="p" variant="heading2xl">{stats.overdueForShipment}</Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Orders overdue for delivery</Text>
                <Text as="p" variant="heading2xl">{stats.overdueForDelivery}</Text>
                <Button
                  variant="plain"
                  onClick={() => navigate('/orders?overdue=true')}
                >
                  View Orders
                </Button>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
